import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Site content pulled from Shopify Metaobjects (with graceful fallbacks).
 * Every hardcoded piece of UI copy on the site can be overridden by populating
 * the corresponding metaobject in Shopify Admin.
 *
 * See /app/SHOPIFY_CONTENT_SETUP.md for the field/metaobject taxonomy.
 */

// Client-side fallbacks — mirror the backend defaults so first paint has real
// content even before the network request lands.
export const CONTENT_FALLBACK = {
  site_settings: {
    brand_name: "Scentodore",
    tagline_pill: "Elevate Every Impression",
    email: "hello@scentodore.com",
    phone: "",
    address: "Vernon, British Columbia · Canada",
    social_instagram: "",
    social_twitter: "",
    social_youtube: "",
    social_tiktok: "",
    footer_description:
      "French-inspired fragrance notes, hand-poured with love in Canada. Modern luxury impressions of iconic scents \u2014 without the mark-ups.",
    newsletter_headline:
      "Join the Scentodore Society for private launches, seasonal essays, and a $15 sample voucher.",
    newsletter_eyebrow: "The Scentodore Society",
    logo_image_url: "/scentodore-logo.webp",
    logo_small_url: "/scentodore-logo-small.webp",
    hero_bottle_image_url: "/scentodore-hero-bottle.webp",
  },
  hero: {
    eyebrow: "Elevate Every Impression",
    headline_line1: "High Luxury",
    headline_line2: "Scents.",
    headline_accent: "Reimagined.",
    body:
      "French-inspired fragrance notes, hand-poured with love in Canada. Signature compositions offered as 50ml & 100ml extraits, pure-oil roll-ons, and discovery samples \u2014 delivered to your door.",
    cta_primary_label: "Explore Collection",
    cta_primary_link: "/shop",
    cta_secondary_label: "Take the Scent Quiz",
    cta_secondary_link: "/quiz",
    trust_bar: ["Hand-poured in Canada", "Cruelty-free", "Free sample"],
    vertical_left_top: "Extrait de Parfum",
    vertical_left_bottom: "25\u201335% Concentration",
    vertical_right_top: "50ml \u00b7 1.7 fl oz",
    vertical_right_bottom: "N\u00b0 MMXXV \u00b7 \u00c9dition",
  },
  marquee: {
    ingredients: [
      "Bergamot", "Ambergris", "Bulgarian Rose", "Oud", "Iris Pallida",
      "Vetiver", "Sandalwood", "Cardamom", "Ylang Ylang", "Tonka Bean",
      "Cedarwood", "Saffron", "Frankincense", "Jasmine Sambac",
      "Patchouli", "Mandarin",
    ],
    offers: [
      "Free Shipping on Orders Over $120",
      "Personalised Engraving Available \u00b7 Add-On",
      "Pure Oils \u00b7 10ml Roll-Ons Available",
      "Hand-Blended in Small Batches",
      "25\u201335% Oil Concentration",
      "Long-Lasting Wear",
      "Cruelty-Free & Vegan",
      "Free 2ml Sample with Every Order",
      "French-Inspired Fragrance Notes \u00b7 Hand-Poured with Love in Canada",
    ],
  },
  sections: {
    featured_eyebrow: "The Collection",
    featured_title: "Signature scents.",
    featured_view_all_label: "View all →",
    social_proof_eyebrow: "Society Says",
    social_proof_title: "4.9 out of 5. Worn by 12,000+ collectors.",
    social_proof_body:
      "Every review verified. Every scent hand-blended in small batches. Every gift wrapped in ivory silk.",
    cta_strip_title: "Not sure where to start?",
    cta_strip_body:
      "Answer 5 questions. Our Master Perfumer AI will match you to your signature within 60 seconds.",
    cta_strip_button: "Take the Scent Quiz",
  },
  testimonials: [
    { quote: "Lasted 12+ hours on my coat. My colleagues stopped me in the hallway.", author: "Nadia S. — Amber Veil", badge: "12h longevity" },
    { quote: "Better than the fragrance it's inspired by. I've given three as gifts.", author: "Marcus L. — Oud Noir", badge: "Repeat buyer" },
    { quote: "The engraved bottle made it feel like a bespoke commission.", author: "Priya K. — Rose Hour", badge: "Verified purchase" },
  ],
  checkout: {
    handoff_title: "Complete your order securely",
    handoff_body:
      "You'll finalise your delivery address and payment on our PCI-compliant secure checkout — Cards, Apple Pay, Google Pay, and more.",
    feature_1_label: "Secure Payment",
    feature_1_desc: "Cards · Apple Pay · Google Pay · Pay-in-4",
    feature_2_label: "Live Shipping",
    feature_2_desc: "Rates calculated for your address",
    feature_3_label: "30-Day Returns",
    feature_3_desc: "Full refund on unopened bottles",
  },
  footer_links: {
    explore: [
      { label: "All Fragrances", href: "/shop" },
      { label: "50ml Bottles", href: "/shop?size=50ml" },
      { label: "Pure Oils · Roll-Ons", href: "/shop?size=10ml%20Pure%20Oil" },
      { label: "Discovery Samples", href: "/shop?size=10ml%20Sample" },
      { label: "Gift Cards", href: "/gift-cards" },
      { label: "Scent Quiz", href: "/quiz" },
      { label: "Discovery Set Builder", href: "/discovery-builder" },
    ],
    society: [
      { label: "Account & Wardrobe", href: "/account" },
      { label: "Loyalty & Tiers", href: "/loyalty" },
      { label: "Subscriptions", href: "/subscriptions" },
      { label: "Journal", href: "/journal" },
      { label: "Gifting & Engraving", href: "/gifting" },
      { label: "Press Kit", href: "/press" },
    ],
    support: [
      { label: "Contact Us", href: "/contact" },
      { label: "FAQ", href: "/faq" },
      { label: "Shipping & Delivery", href: "/shipping" },
      { label: "Returns & Refunds", href: "/returns" },
      { label: "Track Your Order", href: "/track-order" },
      { label: "Fragrance Care Guide", href: "/care-guide" },
    ],
    company: [
      { label: "Our Story", href: "/about" },
      { label: "The Craft", href: "/craft" },
      { label: "Ingredient Sourcing", href: "/sourcing" },
      { label: "Sustainability", href: "/sustainability" },
      { label: "Wholesale & B2B", href: "/wholesale" },
    ],
    policies: [
      { label: "Privacy", href: "/privacy" },
      { label: "Terms", href: "/terms" },
      { label: "Cookies", href: "/cookies" },
      { label: "Accessibility", href: "/accessibility" },
      { label: "Do Not Sell My Info", href: "/do-not-sell" },
    ],
  },
  // Static page overrides — keyed by route slug. Empty by default so React pages
  // use their hardcoded content. Populate a `page_content` metaobject in Shopify
  // to override title / eyebrow / intro / body per page.
  pages: {},
  // Per-page section overrides — { "about": { "story": [{heading, body}] } }
  // Populate `page_section` metaobjects in Shopify.
  page_sections: {},
  // Per-page feature-card overrides — { "about": { "pillars": [{badge, title, body}] } }
  // Populate `page_feature` metaobjects in Shopify.
  page_features: {},
  // FAQ items — populated from Shopify `faq_item` metaobject list. When empty,
  // the FAQ page falls back to its hardcoded questions.
  faq_items: [],
};

const Ctx = createContext(CONTENT_FALLBACK);

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(CONTENT_FALLBACK);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/site/content")
      .then((res) => {
        if (cancelled || !res.data) return;
        // deep-merge with fallback so any missing key is preserved
        setContent((prev) => ({
          ...prev,
          site_settings: { ...prev.site_settings, ...(res.data.site_settings || {}) },
          hero: { ...prev.hero, ...(res.data.hero || {}) },
          marquee: { ...prev.marquee, ...(res.data.marquee || {}) },
          sections: { ...prev.sections, ...(res.data.sections || {}) },
          checkout: { ...prev.checkout, ...(res.data.checkout || {}) },
          footer_links: { ...prev.footer_links, ...(res.data.footer_links || {}) },
          testimonials: (res.data.testimonials && res.data.testimonials.length > 0)
            ? res.data.testimonials
            : prev.testimonials,
          pages: { ...(prev.pages || {}), ...(res.data.pages || {}) },
          page_sections: { ...(prev.page_sections || {}), ...(res.data.page_sections || {}) },
          page_features: { ...(prev.page_features || {}), ...(res.data.page_features || {}) },
          faq_items: (res.data.faq_items && res.data.faq_items.length > 0)
            ? res.data.faq_items
            : (prev.faq_items || []),
        }));
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[site-content] fetch failed, using fallbacks", err?.response?.status);
      });
    return () => { cancelled = true; };
  }, []);

  return <Ctx.Provider value={content}>{children}</Ctx.Provider>;
}

export function useSiteContent() {
  return useContext(Ctx);
}

/**
 * Return the Shopify-editable overrides for a given static page slug.
 * Falls back to an empty object so pages can `??`-merge with their hardcoded defaults.
 *
 * Usage in a page component:
 *   const override = usePageContent("about");
 *   <PageShell title={override.title || "Fallback title"} ... />
 */
export function usePageContent(slug) {
  const { pages } = useContext(Ctx) || {};
  return (pages && pages[slug]) || {};
}

/** Returns the array of `page_section` overrides for a given page + section key.
 * If Shopify has ≥1 matching entry, that array replaces the hardcoded fallback.
 * Returns [] otherwise (page component should use its hardcoded default). */
export function usePageSections(slug, sectionKey = "default") {
  const { page_sections } = useContext(Ctx) || {};
  const group = (page_sections && page_sections[slug]) || {};
  return group[sectionKey] || [];
}

/** Returns the array of `page_feature` cards for a given page + section key.
 * Same override behaviour as usePageSections. */
export function usePageFeatures(slug, sectionKey = "default") {
  const { page_features } = useContext(Ctx) || {};
  const group = (page_features && page_features[slug]) || {};
  return group[sectionKey] || [];
}

/** FAQ helper — returns the list from Shopify (with `question`, `answer`, `order`)
 * or an empty list. FAQ page uses this to override its default hardcoded Q/A. */
export function useFAQ() {
  const { faq_items } = useContext(Ctx) || {};
  return faq_items || [];
}
